import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'moreMenuDeleteLabel'
	| 'moreMenuPinLabel'
	| 'moreMenuUnpinLabel'
	| 'pinnedConversationsLabel'
	| 'pinLimitPopupTitle'
	| 'pinLimitPopupDescription'
	| 'pinLimitPopupDescriptionWithLimit'
	| 'moreMenuRenameLabel'
	| 'moreMenuMoveToSpaceLabel'
	| 'moveToSpaceCreateNewLabel'
	| 'moveToSpaceLoadingLabel'
	| 'moveToSpaceNoSpacesLabel'
	| 'moveToSpaceNoMatchesLabel'
	| 'moveToSpaceSearchInputLabel'
	| 'moveToSpaceSearchPlaceholder'
	| 'viewAllConversationsLabel'
	| 'agentDescriptionLabel'
	| 'runningLozengeLabel'
	| 'footerSeparator'
	| 'scopesOverflowCount'
	| 'renameInputLabel'
	| 'confirmRenameLabel'
	| 'cancelRenameLabel'
	| 'filePreviewFallbackDescription';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		moreMenuDeleteLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.more-menu.delete',
			defaultMessage: 'Delete',
			description:
				'Menu item label displayed in a three-dot more menu in a chat conversation list item within the side navigation of the AI conversation interface. Shown as clickable text in a dropdown menu. When clicked, it opens a confirmation dialog and then permanently deletes the conversation. The purpose is to provide users with a way to remove conversations they no longer need from the side navigation.',
		},
		moreMenuPinLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.more-menu.pin',
			defaultMessage: 'Pin',
			description:
				'Menu item label displayed in a three-dot more menu in a chat conversation list item within the side navigation. When clicked, it pins the conversation so it remains near the top of the list.',
		},
		moreMenuUnpinLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.more-menu.unpin',
			defaultMessage: 'Unpin',
			description:
				'Menu item label displayed in a three-dot more menu in a chat conversation list item within the side navigation. When clicked, it unpins a previously pinned conversation.',
		},
		pinnedConversationsLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.pinned',
			defaultMessage: 'Pinned',
			description:
				'Section heading for pinned chat conversations in the side navigation chat list.',
		},
		pinLimitPopupTitle: {
			id: 'ai-mate.rovo-navigation.chat-history-items.pin-limit-popup.title',
			defaultMessage: "You've reached your pin limit",
			description:
				'Title text displayed in a popup when a user tries to pin another chat conversation after reaching the maximum number of pinned conversations.',
		},
		pinLimitPopupDescription: {
			id: 'ai-mate.rovo-navigation.chat-history-items.pin-limit-popup.description',
			defaultMessage: 'To pin a new chat, unpin an old one.',
			description:
				'Description text displayed in a popup when a user tries to pin another chat conversation after reaching the maximum number of pinned conversations.',
		},
		pinLimitPopupDescriptionWithLimit: {
			id: 'ai-mate.rovo-navigation.chat-history-items.pin-limit-popup.description-with-limit',
			defaultMessage:
				'Up to {limit, plural, one {# chat} other {# chats}} can be pinned at a time. To pin a new chat, unpin an old one.',
			description:
				'Description text displayed in a popup when a user tries to pin another chat conversation after reaching the maximum number of pinned conversations. The limit placeholder is the configured maximum number of pinned conversations.',
		},
		moreMenuRenameLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.more-menu.rename',
			defaultMessage: 'Rename',
			description:
				'Menu item label displayed in a three-dot more menu in a chat conversation list item within the side navigation of the AI conversation interface. Shown as clickable text in a dropdown menu. When clicked, it opens a dialog or input field allowing users to rename the conversation. The purpose is to provide users with a way to change the name of a conversation for better organization in the side navigation.',
		},
		moreMenuMoveToSpaceLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.more-menu.move-to-space',
			defaultMessage: 'Move to space',
			description:
				'Menu item label displayed in a three-dot more menu in a chat conversation list item within the side navigation. When clicked, opens a submenu listing available Rovo spaces the user can move this conversation into.',
		},
		moveToSpaceCreateNewLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.move-to-space.create-new',
			defaultMessage: 'Create new space',
			description:
				'Button label at the bottom of the Move to space submenu. When clicked, opens the Create Space dialog so the user can create a new space and the current conversation will be added to it.',
		},
		moveToSpaceLoadingLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.move-to-space.loading',
			defaultMessage: 'Loading spaces\u2026',
			description:
				'Loading text shown inside the Move to space submenu while the list of spaces is being fetched from the server.',
		},
		moveToSpaceNoSpacesLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.move-to-space.no-spaces',
			defaultMessage: 'No spaces yet',
			description:
				'Empty state text shown inside the Move to space submenu when the user has no Rovo spaces yet.',
		},
		moveToSpaceNoMatchesLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.move-to-space.no-matches',
			defaultMessage: 'No spaces match \u201C{query}\u201D',
			description:
				'Empty state text shown inside the Move to space submenu when the user has typed a search query that does not match any of their existing Rovo spaces. The {query} placeholder is replaced with the user-typed search text and is wrapped in curly double-quotes.',
		},
		moveToSpaceSearchInputLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.move-to-space.search-input-label',
			defaultMessage: 'Search spaces',
			description:
				'Accessible label for the search input at the top of the Move to space submenu. Used by screen readers to announce the purpose of the input field, which lets the user filter the list of Rovo spaces by name.',
		},
		moveToSpaceSearchPlaceholder: {
			id: 'ai-mate.rovo-navigation.chat-history-items.move-to-space.search-placeholder',
			defaultMessage: 'Search spaces',
			description:
				'Placeholder text shown inside the search input at the top of the Move to space submenu before the user starts typing. Hints that typing will filter the list of Rovo spaces shown below.',
		},
		viewAllConversationsLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.view-all-conversations',
			defaultMessage: 'View all conversations',
			description:
				'Link text or button label displayed in the side navigation chat list within the AI conversation interface that opens a full view of all conversations. Shown as clickable text or button label. When clicked, it expands or navigates to show all conversations, not just the limited list shown in the side navigation. The purpose is to provide users with access to their complete conversation history when the side navigation only shows a subset.',
		},
		agentDescriptionLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.agent-description',
			defaultMessage: '{agentName} \u00B7 {relativeTime}',
			description:
				'Description text displayed below a conversation name in the side navigation chat list. Shows the agent name and relative time of the last message, separated by a middle dot. For example: "My Agent \u00B7 2 hours ago".',
		},
		runningLozengeLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.conversation-status-lozenge',
			defaultMessage: 'Running',
			description:
				'Status label text displayed on a badge or lozenge next to a conversation in the side navigation chat list within the AI conversation interface, indicating that the conversation is currently active or processing. Shown as text on a status badge. The purpose is to visually indicate which conversation is currently running or being processed by the AI assistant.',
		},
		footerSeparator: {
			id: 'ai-mate.rovo-navigation.chat-history-items.footer-separator',
			defaultMessage: '\u00B7',
			description:
				'Middle dot separator used between footer items in a chat conversation list item. For example between agent name, scopes, and timestamp.',
		},
		scopesOverflowCount: {
			id: 'ai-mate.rovo-navigation.chat-history-items.scopes-overflow-count',
			defaultMessage: '+{count}',
			description:
				'Text showing the number of additional scopes beyond the first one displayed in the footer of a chat conversation list item. For example, if there are 3 scopes, the first is shown and this displays "+2".',
		},
		renameInputLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.rename-input.label',
			defaultMessage: 'Edit chat name',
			description:
				'Accessible label for the text input field that appears when a user renames a chat conversation in the side navigation. Used by screen readers to announce the purpose of the input field.',
		},
		confirmRenameLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.rename-confirm.label',
			defaultMessage: 'Confirm rename',
			description:
				'Accessible label for the confirm button in the rename chat conversation form in the side navigation. Used by screen readers to announce the purpose of the button.',
		},
		cancelRenameLabel: {
			id: 'ai-mate.rovo-navigation.chat-history-items.rename-cancel.label',
			defaultMessage: 'Cancel rename',
			description:
				'Accessible label for the cancel button in the rename chat conversation form in the side navigation. Used by screen readers to announce the purpose of the button.',
		},
		filePreviewFallbackDescription: {
			id: 'ai-mate.rovo-navigation.chat-history-items.file-preview-fallback',
			defaultMessage: 'Preview is not available for this file type.',
			description:
				'Message shown in the file preview modal when the file cannot be rendered inline (non-image, non-PDF).',
		},
	});

export default message;
